import useLocalization from "@/hooks/useLocalization";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { getReservationDisplayId } from "mt-shared/reservationDisplayId";
import { useCallback, useMemo } from "react";

type GroupIdBadgeProps = {
  groupId: string;
};

export default function GroupIdBadge({ groupId }: GroupIdBadgeProps) {
  const { t } = useLocalization();
  const groupIdPrefix = useMemo(
    () => getReservationDisplayId(groupId),
    [groupId],
  );

  const handleCopyGroupIdPrefix = useCallback(() => {
    if (!navigator.clipboard || !groupIdPrefix) return;
    void navigator.clipboard.writeText(groupIdPrefix);
  }, [groupIdPrefix]);

  if (!groupIdPrefix) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 1.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "action.selected",
      }}
    >
      <Typography
        sx={{
          px: 1.5,
          py: 0.5,
          fontFamily: "monospace",
          fontSize: "1.25rem",
          lineHeight: 1,
          letterSpacing: "0.06em",
          color: "text.primary",
        }}
      >
        {groupIdPrefix}
      </Typography>
      <Box
        sx={{
          width: "1px",
          alignSelf: "stretch",
          bgcolor: "divider",
        }}
      />
      <Box sx={{ display: "flex", alignSelf: "stretch" }}>
        <Tooltip title={t("reservation:copyGroupId")}>
          <IconButton
            size="small"
            onClick={handleCopyGroupIdPrefix}
            aria-label={t("reservation:copyGroupId")}
            sx={{
              borderRadius: 0,
              px: 1,
              color: "text.primary",
              height: "100%",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
